// Import the functions you need from the SDKs you need
import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getAuth } from "firebase/auth";

// ============================================
// PRIMARY PROJECT (Database & Storage)
// Project: arztaleb-18dde
// ============================================
const primaryFirebaseConfig = {
  apiKey: "AIzaSyAxzPHGckoEhAxEuAtVq5xIURPK90CsLmY",
  authDomain: "arztaleb-18dde.firebaseapp.com",
  projectId: "arztaleb-18dde",
  storageBucket: "arztaleb-18dde.firebasestorage.app",
  messagingSenderId: "332372915018",
  appId: "1:332372915018:web:9e1d074f74e703c45e07b3",
  measurementId: "G-LXSG0WH6W4"
};

// ============================================
// AUTH PROJECT (Authentication Only)
// Project: arztalebhost (Project ID: arztalebhost)
// Project Number: 732814116030
// Billing: Blaze (Pay as you go) - Required for phone verification
// ============================================
const authFirebaseConfig = {
  apiKey: "AIzaSyBeg16yKIjIrJ9usqXZ1WCecDQPg5KhhS4",
  authDomain: "arztalebhost.firebaseapp.com",
  projectId: "arztalebhost",
  messagingSenderId: "732814116030",
  appId: "1:732814116030:web:13441b555ad8062863caef",
  measurementId: "G-NW3PZZWFL7"
};

// Debug logging for Firebase
function firebaseLog(message: string, type: 'info' | 'warn' | 'error' = 'info') {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}] [FIREBASE] [${type.toUpperCase()}] ${message}`);
}

firebaseLog('Starting Firebase initialization...', 'info');

// Initialize Primary Firebase App (for database and storage)
let primaryApp: FirebaseApp | null = null;
try {
const existingPrimaryApp = getApps().find(app => app.name === '[DEFAULT]');
if (existingPrimaryApp) {
  primaryApp = existingPrimaryApp;
    firebaseLog('Using existing primary Firebase app', 'info');
} else {
    firebaseLog('Initializing primary Firebase app...', 'info');
  primaryApp = initializeApp(primaryFirebaseConfig);
    firebaseLog('Initialized primary Firebase app successfully', 'info');
  }
} catch (error) {
  firebaseLog('Failed to initialize primary Firebase app: ' + (error instanceof Error ? error.message : String(error)), 'error');
  console.error('Firebase initialization error details:', error);
  // Try to continue with existing app
  const existingPrimaryApp = getApps().find(app => app.name === '[DEFAULT]');
  if (existingPrimaryApp) {
    primaryApp = existingPrimaryApp;
    firebaseLog('Using existing primary Firebase app after error', 'warn');
  } else {
    firebaseLog('Cannot recover from Firebase initialization error - app will continue without Firebase', 'error');
    // Don't throw - let the app continue and Firebase will be initialized when needed
  }
}

// Initialize Auth Firebase App (separate project for authentication)
let authApp: FirebaseApp | null = null;
try {
const existingAuthApp = getApps().find(app => app.name === 'auth');
if (existingAuthApp) {
  authApp = existingAuthApp;
    firebaseLog('Using existing auth Firebase app', 'info');
} else {
    firebaseLog('Initializing auth Firebase app...', 'info');
  authApp = initializeApp(authFirebaseConfig, 'auth');
    firebaseLog('Initialized auth Firebase app successfully', 'info');
  }
} catch (error) {
  firebaseLog('Failed to initialize auth Firebase app: ' + (error instanceof Error ? error.message : String(error)), 'error');
  console.error('Auth Firebase initialization error details:', error);
  // Try to continue with existing app
  const existingAuthApp = getApps().find(app => app.name === 'auth');
  if (existingAuthApp) {
    authApp = existingAuthApp;
    firebaseLog('Using existing auth Firebase app after error', 'warn');
  } else {
    // Use primary app as fallback
    authApp = primaryApp;
    firebaseLog('Using primary app as auth fallback', 'warn');
  }
}

// Initialize Analytics (only in browser environment) - from primary app
let analytics;
if (typeof window !== 'undefined' && primaryApp) {
  try {
    // Only initialize Analytics in browser (not in Android WebView)
    // Check if we're in a Capacitor environment
    const isCapacitor = typeof (window as any).Capacitor !== 'undefined';
    if (!isCapacitor) {
  analytics = getAnalytics(primaryApp);
      console.log('Analytics initialized');
    } else {
      console.log('Skipping Analytics in Capacitor environment');
    }
  } catch (error) {
    console.warn('Firebase Analytics initialization failed:', error);
    // Analytics is optional, continue without it
  }
} else {
  console.log('Skipping Analytics - window or primaryApp not available');
}

// Initialize Firestore - from PRIMARY project (arztaleb-18dde)
let db;
if (primaryApp) {
  try {
    firebaseLog('Initializing Firestore...', 'info');
    db = getFirestore(primaryApp);
    firebaseLog('Firestore initialized successfully', 'info');
  } catch (error) {
    firebaseLog('Failed to initialize Firestore: ' + (error instanceof Error ? error.message : String(error)), 'error');
    console.error('Firestore initialization error details:', error);
    // Try to re-initialize app and try again
    try {
      firebaseLog('Retrying Firestore initialization...', 'warn');
      primaryApp = initializeApp(primaryFirebaseConfig);
      db = getFirestore(primaryApp);
      firebaseLog('Firestore initialized on retry', 'info');
    } catch (retryError) {
      firebaseLog('Failed to re-initialize Firestore: ' + (retryError instanceof Error ? retryError.message : String(retryError)), 'error');
      console.error('Firestore retry error details:', retryError);
      // Don't throw - Firestore will be initialized when needed
    }
  }
} else {
  firebaseLog('Primary app not initialized, Firestore will be initialized when needed', 'warn');
}

// Initialize Storage - from PRIMARY project (arztaleb-18dde)
let storage;
if (primaryApp) {
  try {
    firebaseLog('Initializing Storage...', 'info');
    storage = getStorage(primaryApp);
    firebaseLog('Storage initialized successfully', 'info');
  } catch (error) {
    firebaseLog('Failed to initialize Storage: ' + (error instanceof Error ? error.message : String(error)), 'error');
    console.error('Storage initialization error details:', error);
    // Don't throw - Storage will be initialized when needed
  }
} else {
  firebaseLog('Primary app not initialized, Storage will be initialized when needed', 'warn');
}

// Initialize Auth - from AUTH project (arztalebhost)
let auth;
if (authApp) {
  try {
    firebaseLog('Initializing Auth...', 'info');
    auth = getAuth(authApp);
    firebaseLog('Auth initialized successfully', 'info');
  } catch (error) {
    firebaseLog('Failed to initialize Auth: ' + (error instanceof Error ? error.message : String(error)), 'error');
    console.error('Auth initialization error details:', error);
    // Try to re-initialize auth app
    try {
      if (authApp) {
        firebaseLog('Retrying Auth initialization...', 'warn');
        authApp = initializeApp(authFirebaseConfig, 'auth');
        auth = getAuth(authApp);
        firebaseLog('Auth initialized on retry', 'info');
      }
    } catch (retryError) {
      firebaseLog('Failed to re-initialize Auth: ' + (retryError instanceof Error ? retryError.message : String(retryError)), 'error');
      console.error('Auth retry error details:', retryError);
      // Use primary app auth as last resort
      if (primaryApp) {
        try {
          firebaseLog('Trying Auth with primary app...', 'warn');
          auth = getAuth(primaryApp);
          firebaseLog('Auth initialized with primary app', 'info');
        } catch (finalError) {
          firebaseLog('Failed to initialize Auth with primary app: ' + (finalError instanceof Error ? finalError.message : String(finalError)), 'error');
          console.error('Auth final error details:', finalError);
          // Don't throw - Auth will be initialized when needed
        }
      } else {
        firebaseLog('Cannot initialize Auth - no apps available', 'warn');
      }
    }
  }
} else {
  firebaseLog('Auth app not initialized, Auth will be initialized when needed', 'warn');
}

// Initialize Auth on the PRIMARY project (arztaleb-18dde).
// This is the auth instance that Firestore security rules can see
// (rules only trust tokens issued by the same project as the database).
let primaryAuth;
if (primaryApp) {
  try {
    primaryAuth = getAuth(primaryApp);
    firebaseLog('Primary-project Auth initialized successfully', 'info');
  } catch (error) {
    firebaseLog('Failed to initialize primary-project Auth: ' + (error instanceof Error ? error.message : String(error)), 'error');
  }
}

firebaseLog('Firebase initialization complete', 'info');

export { primaryApp, authApp, analytics, db, storage, auth, primaryAuth, primaryFirebaseConfig };

